import React, { useState } from 'react';
import { fetchPxwConnectorData } from '../../_lib/utils';
import useDataService from '../../hooks/useDataService';
import { eMessageType } from '../../types/IMessageType';
import { PxwConnectorFormData } from './types';
import { getTaskParams } from './utils';

export type TaskStatus = 'idle' | 'loading' | 'success' | 'error';

export const useConnectorTasks = (formData: PxwConnectorFormData, _setFormData: React.Dispatch<React.SetStateAction<PxwConnectorFormData>>) => {
  const { openSnackBar } = useDataService();
  const [statuses, setStatuses] = useState<Record<string, TaskStatus>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isRunningAll, setIsRunningAll] = useState(false);

  const runSingleTask = async (taskId: string): Promise<boolean> => {
    setStatuses((prev) => ({ ...prev, [taskId]: 'loading' }));
    setErrors((prev) => ({ ...prev, [taskId]: '' }));
    try {
      if (taskId === 'deploy-build') {
        await new Promise((resolve) => setTimeout(resolve, 500));
        openSnackBar('Task completed successfully', eMessageType.success);
        setStatuses((prev) => ({ ...prev, [taskId]: 'success' }));
        return true;
      }
      const { url, baseUrl, payload } = getTaskParams(taskId, formData);
      const res: any = await fetchPxwConnectorData('post', url, payload, baseUrl);
      if (res?.error) {
        const errorMsg = res.message || (typeof res.error === 'string' ? res.error : 'API call failed');
        openSnackBar(errorMsg, eMessageType.error);
        setStatuses((prev) => ({ ...prev, [taskId]: 'error' }));
        setErrors((prev) => ({ ...prev, [taskId]: String(errorMsg) }));
        return false;
      }
      openSnackBar('Task completed successfully', eMessageType.success);
      setStatuses((prev) => ({ ...prev, [taskId]: 'success' }));
      return true;
    } catch (err: any) {
      const msg = err.message || 'API call failed';
      openSnackBar(msg, eMessageType.error);
      setStatuses((prev) => ({ ...prev, [taskId]: 'error' }));
      setErrors((prev) => ({ ...prev, [taskId]: String(msg) }));
      return false;
    }
  };

  const runAllTasks = async () => {
    setIsRunningAll(true);
    const tasksToRun = [
      ...(formData.tenant.configureQuickBooks ? ['qbo-configurator', 'qbo-setup'] : []),
      ...(formData.tenant.configureShipStation ? ['shipstation'] : []),
      ...(formData.tenant.configureDiagnostic ? ['diagnostic'] : []),
    ];
    for (const taskId of tasksToRun) {
      if (!(await runSingleTask(taskId))) break;
    }
    setIsRunningAll(false);
  };

  const resetTaskStatus = (taskId: string) => {
    setStatuses((prev) => ({ ...prev, [taskId]: 'idle' }));
  };

  return { statuses, errors, isRunningAll, runSingleTask, runAllTasks, resetTaskStatus };
};
